import React, { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTheme, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MonthlyDonationProgramCard from "../../../components/common/MonthlyDonationProgramCard";
import { getMonthlyDonations } from "../../../api/services/monthlyDonationService";
import { Images } from "../../../utils/Images";

const MonthlyDonationProgram = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery("(min-width: 600px) and (max-width: 835px)");
  const isTablet = useMediaQuery("(min-width: 835px) and (max-width: 950px)");
  const isDesktop = useMediaQuery("(min-width: 950px)");
  const navigate = useNavigate();
  const home = useSelector((state) => state.cms.home);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === 81);
    return {
      label: section?.label || "Join Monthly",
      title: section?.title || "Monthly Donation Programs",
      description:
        section?.description ||
        "Monthly Donation Programs empower you to create lasting change with consistent support. Your monthly contributions ensure ongoing care, stability, and hope for those who need it most, making a real difference every single day.",
    };
  }, [home]);

  useEffect(() => {
    let isMounted = true;
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const res = await getMonthlyDonations({ limit: 3 });
        if (isMounted) setCampaigns(res?.data || []);
      } catch (err) {
        console.error("MonthlyDonationProgram fetch error:", err);
        if (isMounted) setCampaigns([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchCampaigns();
    return () => { isMounted = false; };
  }, []);

  return (
    <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "40px", marginBottom: "15px" }}>
      <div
        style={{
          width: isMobile ? "calc(100% - 32px)" : "94.44%",
          maxWidth: "1368px",
          backgroundColor: "rgba(244, 241, 241, 0.36)",
          borderRadius: "20px",
          boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
          position: "relative",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: isMobile ? "32px 16px 40px" : "31px 34px 35px",
        }}
      >
        {/* Top-left sparkle decoration */}
        {!isMobile && (
          <img
            src={Images.orangeSparks}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "68px",
              left: "64px",
              width: "49.8429px",
              height: "71.2042px",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        )}

        {/* Top-right heart decoration */}
        {!isMobile && (
          <img
            src={Images.heart}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "75px",
              right: "180px",
              width: "26.8898px",
              height: "28.1627px",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        )}

        {/* Section header */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            marginBottom: isMobile ? "24px" : "32px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Label */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", marginTop: "12px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#A9C74E",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: "Arial, sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                letterSpacing: "2px",
                textTransform: "uppercase",
                color: "#131313",
                lineHeight: "14px",
              }}
            >
              {cms.label}
            </span>
          </div>

          {/* Title */}
          <h2
            style={{
              fontFamily: "'Red Hat Display', sans-serif",
              fontWeight: 700,
              fontSize: isMobile ? "22px" : "36px",
              lineHeight: 1.4,
              letterSpacing: "-0.36px",
              color: "#1D1D1B",
              textTransform: "capitalize",
              textAlign: "center",
              margin: "0 0 16px 0",
            }}
          >
            {cms.title}
          </h2>

          {/* Description */}
          <p
            style={{
              fontFamily: "'Red Hat Display', sans-serif",
              fontWeight: 400,
              fontSize: isMobile ? "14px" : "16px",
              lineHeight: "24px",
              color: "#575555",
              textAlign: "center",
              maxWidth: "995px",
              margin: 0,
              padding: isMobile ? "0 8px" : 0,
            }}
          >
            {cms.description}
          </p>
        </div>

        {/* Campaign cards */}
        <div
          style={{
            display: "flex",
            flexWrap: isMobile || isMediumScreen ? "nowrap" : "wrap",
            justifyContent: "flex-start",
            gap: isMobile ? "16px" : "18px",
            width: "100%",
            overflowX: isMobile || isMediumScreen ? "auto" : "visible",
            marginTop: "15px",
            marginBottom: isMobile ? "24px" : "48px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            position: "relative",
            zIndex: 1,
          }}
        >
          {loading ? (
            Array.from({ length: isMobile ? 1 : 3 }).map((_, i) => (
              <div
                key={`mdp-skel-${i}`}
                style={{
                  width: isMobile ? "280px" : "417px",
                  minWidth: isMobile ? "280px" : "417px",
                  height: "414px",
                  background: "#E0E0E0",
                  borderRadius: "16px",
                  opacity: 0.6,
                  flexShrink: 0,
                }}
              />
            ))
          ) : campaigns.length ? (
            campaigns.map((card) => (
              <div key={card.id} style={{ flexShrink: 0 }}>
                <MonthlyDonationProgramCard
                  {...card}
                  isMobile={isMobile}
                  isMediumScreen={isMediumScreen}
                  isTablet={isTablet}
                  isDesktop={isDesktop}
                />
              </div>
            ))
          ) : (
            <div
              style={{
                width: "100%",
                padding: "40px 20px",
                textAlign: "center",
                color: "#666666",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              No monthly donation programs available.
            </div>
          )}
        </div>

        {/* View More button */}
        <div style={{ position: "relative", zIndex: 1, marginTop: "-15px" }}>
          <button
            onClick={() => navigate("/monthly-donation")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              backgroundColor: "#1D1D1B",
              color: "#FFFFFF",
              fontFamily: "'Red Hat Display', sans-serif",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "14px",
              border: "none",
              borderRadius: "10px",
              padding: "15px 24px",
              cursor: "pointer",
              boxShadow: "0px 4px 0px #b9b9b8",
              whiteSpace: "nowrap",
              minWidth: "161px",
            }}
          >
            View More
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.83337 14.1667L14.1667 5.83334M14.1667 5.83334H5.83337M14.1667 5.83334V14.1667"
                stroke="white"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthlyDonationProgram;
